import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  TrendingUp, TrendingDown, ArrowUpRight, Calendar, ChevronDown, Activity,
  ShoppingCart, ShoppingBag, Wallet, FileText, Receipt, BadgeDollarSign,
  RotateCcw, ArrowLeftRight, Package, AlertTriangle, Sparkles,
} from "lucide-react";
import { api, getUser } from "../../../lib/auth";
import { getCurrencySymbol } from "../../../lib/currency";

// ─── COLORS (brand green primary) ───
const C = {
  primary: "#1FA868",
  primaryDark: "#16834D",
  primaryDeep: "#0F5132",
  primarySoft: "#E8F5E9",
  primaryFaint: "#F0FAF3",
};

const fmt = (n) => `${getCurrencySymbol()}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtK = (n) => {
  const v = Number(n || 0);
  const sym = getCurrencySymbol();
  if (v >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${sym}${(v / 1000).toFixed(1)}K`;
  return `${sym}${v.toLocaleString()}`;
};

// ─── Smooth area chart (Sales vs Purchases over 6 months) ───
function AreaChart({ data = [] }) {
  if (!data.length) return null;
  const W = 600, H = 220, P = { l: 38, r: 16, t: 22, b: 30 };
  const max = Math.max(1, ...data.flatMap((d) => [d.sales, d.purchases])) * 1.15;
  const x = (i) => P.l + (i / (data.length - 1)) * (W - P.l - P.r);
  const y = (v) => P.t + (1 - v / max) * (H - P.t - P.b);

  const pathFor = (k) => {
    if (data.length < 2) return "";
    const pts = data.map((d, i) => [x(i), y(d[k])]);
    let p = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 1; i < pts.length; i++) {
      const [px, py] = pts[i - 1], [cx, cy] = pts[i];
      const mx = (px + cx) / 2;
      p += ` C ${mx} ${py}, ${mx} ${cy}, ${cx} ${cy}`;
    }
    return p;
  };
  const areaFor = (k) => {
    const line = pathFor(k);
    if (!line) return "";
    const last = x(data.length - 1), first = x(0);
    return `${line} L ${last} ${y(0)} L ${first} ${y(0)} Z`;
  };

  const yTicks = [0, 0.33, 0.66, 1].map((t) => Math.round(max * t));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full">
      <defs>
        <linearGradient id="gradSales" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.primary} stopOpacity="0.35" />
          <stop offset="100%" stopColor={C.primary} stopOpacity="0" />
        </linearGradient>
        <linearGradient id="gradPurch" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Y-grid */}
      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={P.l} x2={W - P.r} y1={y(v)} y2={y(v)} stroke="#E5E7EB" strokeDasharray="3 4" />
          <text x={P.l - 6} y={y(v) + 4} fontSize="9" textAnchor="end" fill="#9CA3AF">{v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}</text>
        </g>
      ))}
      {/* Areas */}
      <path d={areaFor("purchases")} fill="url(#gradPurch)" />
      <path d={areaFor("sales")} fill="url(#gradSales)" />
      {/* Lines */}
      <path d={pathFor("purchases")} fill="none" stroke="#0EA5E9" strokeWidth="2" />
      <path d={pathFor("sales")} fill="none" stroke={C.primary} strokeWidth="2.5" />
      {/* Dots + labels */}
      {data.map((d, i) => (
        <g key={i}>
          <circle cx={x(i)} cy={y(d.sales)} r="3.5" fill="white" stroke={C.primary} strokeWidth="2" />
          <circle cx={x(i)} cy={y(d.purchases)} r="3" fill="white" stroke="#0EA5E9" strokeWidth="1.5" />
          <text x={x(i)} y={H - 10} fontSize="10" textAnchor="middle" fill="#6B7280" fontWeight="500">{d.label}</text>
        </g>
      ))}
    </svg>
  );
}

// ─── Donut for sales-mix vs returns ───
function MixDonut({ sales, returns, due }) {
  const total = Math.max(1, sales + returns + due);
  const segs = [
    { v: sales, color: C.primary, lbl: "Net Sales" },
    { v: due, color: "#F59E0B", lbl: "Outstanding" },
    { v: returns, color: "#EF4444", lbl: "Returns" },
  ];
  const R = 60, CIRC = 2 * Math.PI * R;
  let off = 0;
  return (
    <div className="flex items-center gap-5">
      <div className="relative w-[130px] h-[130px] flex-shrink-0">
        <svg width="130" height="130" viewBox="0 0 130 130" className="-rotate-90">
          <circle cx="65" cy="65" r={R} fill="none" stroke="#F3F4F6" strokeWidth="14" />
          {segs.map((s, i) => {
            const len = (s.v / total) * CIRC;
            const el = (
              <circle key={i} cx="65" cy="65" r={R} fill="none" stroke={s.color} strokeWidth="14"
                strokeDasharray={`${len} ${CIRC - len}`} strokeDashoffset={-off} />
            );
            off += len;
            return el;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-[10px] uppercase tracking-wider text-black/45 font-semibold">Net</div>
          <div className="font-display font-extrabold text-base text-black tabular-nums">{fmtK(sales)}</div>
        </div>
      </div>
      <div className="flex-1 space-y-2">
        {segs.map((s) => (
          <div key={s.lbl} className="flex items-center justify-between text-sm">
            <span className="inline-flex items-center gap-2 text-black/65">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />{s.lbl}
            </span>
            <span className="font-semibold tabular-nums text-black">{fmt(s.v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Tiny inline sparkline ───
function Spark({ data, color = C.primary }) {
  if (!data?.length) return null;
  const W = 90, H = 30;
  const max = Math.max(1, ...data);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * W},${H - (v / max) * (H - 4) - 2}`).join(" ");
  return (
    <svg width={W} height={H} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── KPI tile ───
function Kpi({ lbl, value, delta, icon: Icon, accent, sparkline, testid }) {
  const isCurrency = !["Invoices"].includes(lbl);
  const display = isCurrency ? fmt(value) : Number(value || 0).toLocaleString();
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-black/[0.06] p-5 hover:border-black/15 hover:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] transition-all">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent.bg} ${accent.fg}`}>
          <Icon size={17} />
        </div>
        {delta !== undefined && (
          <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold ${delta >= 0 ? "text-[#1FA868]" : "text-rose-500"}`}>
            {delta >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
      <div className="mt-4">
        <div className="text-[11px] text-black/50 font-medium uppercase tracking-wider">{lbl}</div>
        <div className="font-display font-extrabold text-2xl text-black tracking-tight mt-0.5 truncate">{display}</div>
      </div>
      {sparkline && <div className="mt-2 -mb-1"><Spark data={sparkline} /></div>}
    </div>
  );
}

export default function AccountDashboard() {
  const { subdomain } = useParams();
  const [data, setData] = useState(null);
  const [profitPeriods, setProfitPeriods] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [warehouseId, setWarehouseId] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const user = getUser();

  const load = async () => {
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    if (warehouseId && warehouseId !== "all") params.warehouse = warehouseId;
    const [r, pr] = await Promise.all([
      api.get("/t/overview", { params }),
      api.get("/t/overview/profit"),
    ]);
    setData(r.data);
    setProfitPeriods(pr.data);
    if (!range.start && r.data.range) setRange(r.data.range);
  };
  useEffect(() => { load(); }, [warehouseId]); // eslint-disable-line

  if (!data) {
    return (
      <div className="p-10">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-black/5 rounded" />
          <div className="grid grid-cols-4 gap-4">{[...Array(8)].map((_, i) => <div key={i} className="h-32 bg-black/5 rounded-2xl" />)}</div>
        </div>
      </div>
    );
  }

  const stats = data.stats || {};
  const series = data.sales_purchases_series || [];
  const salesSpark = series.map((m) => m.sales);
  const purchSpark = series.map((m) => m.purchases);
  // Compute simple WoW delta (last vs prev month)
  const last = series.at(-1) || {}; const prev = series.at(-2) || {};
  const dSales = prev.sales ? ((last.sales - prev.sales) / prev.sales) * 100 : 0;
  const dPurch = prev.purchases ? ((last.purchases - prev.purchases) / prev.purchases) * 100 : 0;

  return (
    <div data-testid="tenant-dashboard" className="p-6 lg:p-8 max-w-[1500px]">
      {/* ── HEADER ── (no purple gradient — clean text + filter strip) */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color: C.primary }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.primary }} />
            Live Overview
          </div>
          <h1 className="font-display font-extrabold text-4xl text-black tracking-tight mt-1.5">
            Hi, {(user?.name || "Owner").split(" ")[0]} <span className="inline-block animate-wave">👋</span>
          </h1>
          <p className="text-black/55 text-sm mt-1">Here's how your business is performing today.</p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} data-testid="warehouse-filter"
              className="appearance-none pl-4 pr-9 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold text-black hover:border-black/20 cursor-pointer focus:outline-none focus:border-[#1FA868]">
              <option value="all">All warehouses</option>
              {data.warehouses?.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-black/45" />
          </div>
          <div className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold">
            <Calendar size={13} className="text-black/45" />
            <input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })}
              data-testid="range-start" className="bg-transparent focus:outline-none w-[120px]" />
            <span className="text-black/40">→</span>
            <input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })}
              data-testid="range-end" className="bg-transparent focus:outline-none w-[120px]" />
            <button onClick={load} data-testid="range-apply"
              className="ml-1 px-2.5 py-1 rounded-md text-white text-xs font-bold" style={{ background: C.primary }}>Apply</button>
          </div>
        </div>
      </div>

      {/* ── BENTO HERO ROW ── (2 large feature cards) */}
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        {/* PROFIT — featured dark card with period toggle */}
        <div data-testid="stat-profit" className="lg:col-span-1 relative rounded-2xl overflow-hidden p-6 text-white"
          style={{ background: `linear-gradient(135deg, ${C.primaryDeep} 0%, ${C.primaryDark} 70%, ${C.primary} 130%)` }}>
          <div className="absolute -right-10 -bottom-12 w-44 h-44 rounded-full bg-white/10 blur-2xl" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/80">
                <Sparkles size={11} /> Profit
              </span>
              <div className="flex gap-0.5 bg-white/10 rounded-full p-0.5" data-testid="profit-period-switcher">
                {[
                  { k: "daily", lbl: "D" },
                  { k: "weekly", lbl: "W" },
                  { k: "monthly", lbl: "M" },
                  { k: "yearly", lbl: "Y" },
                ].map((p) => (
                  <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`profit-period-${p.k}`}
                    className={`px-2.5 py-1 text-[10px] font-extrabold rounded-full transition-all ${
                      period === p.k ? "bg-white text-[#1B4332]" : "text-white/70 hover:text-white"
                    }`}>{p.lbl}</button>
                ))}
              </div>
            </div>
            <div className="font-display font-extrabold text-5xl tracking-tight mt-3 tabular-nums">
              {fmt(profitPeriods?.[period]?.profit || 0)}
            </div>
            <div className="text-white/70 text-xs mt-1.5 capitalize">
              {period} · {profitPeriods?.[period]?.orders || 0} {profitPeriods?.[period]?.orders === 1 ? "order" : "orders"} · since {profitPeriods?.[period]?.range_start}
            </div>
            <div className="mt-5 pt-5 border-t border-white/15 grid grid-cols-3 gap-3 text-sm">
              <div><div className="text-white/65 text-[10px] uppercase tracking-wider font-semibold">Sales</div><div className="font-bold mt-0.5">{fmtK(profitPeriods?.[period]?.sales || 0)}</div></div>
              <div><div className="text-white/65 text-[10px] uppercase tracking-wider font-semibold">Purchases</div><div className="font-bold mt-0.5">{fmtK(profitPeriods?.[period]?.purchases || 0)}</div></div>
              <div><div className="text-white/65 text-[10px] uppercase tracking-wider font-semibold">Orders</div><div className="font-bold mt-0.5">{profitPeriods?.[period]?.orders || 0}</div></div>
            </div>
          </div>
        </div>

        {/* TREND CHART */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-black/[0.06] p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="font-display font-bold text-lg text-black">Sales vs Purchases</h3>
              <p className="text-xs text-black/50">Last 6 months · Smoothed trend</p>
            </div>
            <div className="flex gap-3 text-xs">
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.primary }} /> Sales</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#0EA5E9]" /> Purchases</span>
            </div>
          </div>
          <div className="h-56">
            <AreaChart data={series} />
          </div>
        </div>
      </div>

      {/* ── KPI STRIP ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Kpi lbl="Sales" value={stats.sales} delta={dSales} icon={ShoppingCart} sparkline={salesSpark}
          testid="stat-sales" accent={{ bg: "bg-[#E8F5E9]", fg: "text-[#1FA868]" }} />
        <Kpi lbl="Purchases" value={stats.purchases} delta={dPurch} icon={ShoppingBag} sparkline={purchSpark}
          testid="stat-purchases" accent={{ bg: "bg-sky-50", fg: "text-sky-600" }} />
        <Kpi lbl="Sales Return" value={stats.sales_return} icon={RotateCcw}
          testid="stat-sales-return" accent={{ bg: "bg-amber-50", fg: "text-amber-600" }} />
        <Kpi lbl="Purchase Return" value={stats.purchases_return} icon={ArrowLeftRight}
          testid="stat-purchases-return" accent={{ bg: "bg-rose-50", fg: "text-rose-600" }} />
        <Kpi lbl="Sales Due" value={stats.sales_due} icon={Wallet}
          testid="stat-sales-due" accent={{ bg: "bg-violet-50", fg: "text-violet-600" }} />
        <Kpi lbl="Invoices" value={stats.invoice_count} icon={FileText}
          testid="stat-invoice" accent={{ bg: "bg-orange-50", fg: "text-orange-600" }} />
      </div>

      {/* ── BENTO ROW 2 ── */}
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        {/* Sales mix donut */}
        <div className="bg-white rounded-2xl border border-black/[0.06] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-base text-black">Revenue Mix</h3>
            <BadgeDollarSign size={16} className="text-black/35" />
          </div>
          <MixDonut sales={stats.sales} returns={stats.sales_return} due={stats.sales_due} />
        </div>

        {/* Top products */}
        <div className="bg-white rounded-2xl border border-black/[0.06] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-base text-black">Top Products</h3>
            <Package size={16} className="text-black/35" />
          </div>
          <div className="space-y-2.5">
            {(data.top_products || []).slice(0, 5).map((p, i) => {
              const max = Math.max(1, ...(data.top_products || []).slice(0, 5).map((x) => x.sales_count));
              const w = (p.sales_count / max) * 100;
              return (
                <div key={p.id} className="group">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-semibold text-black truncate flex-1 pr-2">
                      <span className="text-black/40 font-bold mr-1.5">#{i + 1}</span>{p.name}
                    </span>
                    <span className="text-xs font-bold text-black tabular-nums">{p.sales_count} sold</span>
                  </div>
                  <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${w}%`, background: `linear-gradient(90deg, ${C.primary}, ${C.primaryDark})` }} />
                  </div>
                </div>
              );
            })}
            {(data.top_products || []).length === 0 && (
              <div className="text-sm text-black/45 py-6 text-center">No products yet.</div>
            )}
          </div>
        </div>

        {/* Quick action buttons / shortcuts */}
        <div className="bg-white rounded-2xl border border-black/[0.06] p-6">
          <h3 className="font-display font-bold text-base text-black mb-4">Quick actions</h3>
          <div className="grid grid-cols-2 gap-2">
            {[
              { lbl: "New Sale", icon: ShoppingCart, to: `/t/${subdomain}/dashboard`, c: C.primary, bg: C.primarySoft },
              { lbl: "Add Product", icon: Package, to: `/t/${subdomain}/dashboard`, c: "#0EA5E9", bg: "#E0F2FE" },
              { lbl: "View Orders", icon: Receipt, to: `/t/${subdomain}/store/orders`, c: "#F59E0B", bg: "#FEF3C7" },
              { lbl: "Storefront", icon: ArrowUpRight, to: `/store/${subdomain}`, ext: true, c: "#8B5CF6", bg: "#EDE9FE" },
            ].map((a) => {
              const I = a.icon;
              return (
                <a key={a.lbl} href={a.to} target={a.ext ? "_blank" : "_self"} rel={a.ext ? "noopener noreferrer" : undefined}
                  data-testid={`quick-${a.lbl.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex flex-col gap-2 p-3.5 rounded-xl bg-[#FAFBFB] hover:shadow-sm border border-transparent hover:border-black/10 transition-all">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: a.bg, color: a.c }}>
                    <I size={15} />
                  </div>
                  <div className="text-sm font-bold text-black">{a.lbl}</div>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── BENTO ROW 3 ── (Recent activity + Low stock alerts side-by-side) */}
      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 bg-white rounded-2xl border border-black/[0.06] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-base text-black">Recent Activity</h3>
            <Activity size={16} className="text-black/35" />
          </div>
          <div className="space-y-1">
            {(data.recent_activity || []).map((a) => {
              const tones = {
                sale: { bg: "bg-[#E8F5E9]", fg: "text-[#1FA868]" },
                stock: { bg: "bg-sky-50", fg: "text-sky-600" },
                customer: { bg: "bg-violet-50", fg: "text-violet-600" },
                order: { bg: "bg-amber-50", fg: "text-amber-600" },
              }[a.type] || { bg: "bg-black/5", fg: "text-black/55" };
              return (
                <div key={a.id} className="flex items-center gap-3 py-2.5 hover:bg-[#FAFBFB] rounded-lg px-2 -mx-2 transition-colors">
                  <div className={`w-9 h-9 rounded-xl ${tones.bg} ${tones.fg} flex items-center justify-center flex-shrink-0`}>
                    <Activity size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-black truncate">{a.title}</div>
                    <div className="text-[11px] text-black/55 truncate">{a.subtitle}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-black/40 font-bold">{(a.created_at || "").slice(11, 16) || "—"}</div>
                </div>
              );
            })}
            {(data.recent_activity || []).length === 0 && (
              <div className="text-sm text-black/45 py-8 text-center">No activity yet.</div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-black/[0.06] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-base text-black">Low Stock Alerts</h3>
            <AlertTriangle size={16} className="text-amber-500" />
          </div>
          <div className="space-y-2.5">
            {(data.top_products || []).slice(0, 4).map((p) => {
              const stock = p.stock || 0;
              const danger = stock < 30;
              const pct = Math.min(100, (stock / 200) * 100);
              return (
                <div key={p.id} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-sm font-semibold text-black truncate flex-1 pr-2">{p.name}</div>
                      <div className={`text-[11px] font-bold tabular-nums ${danger ? "text-rose-500" : "text-black/65"}`}>{stock} left</div>
                    </div>
                    <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, background: danger ? "#EF4444" : C.primary }} />
                    </div>
                  </div>
                </div>
              );
            })}
            {(data.top_products || []).length === 0 && (
              <div className="text-sm text-black/45 py-6 text-center">All stock levels healthy.</div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes wave { 0%, 100% { transform: rotate(0); } 25% { transform: rotate(15deg); } 75% { transform: rotate(-10deg); } }
        .animate-wave { display: inline-block; animation: wave 2s ease-in-out infinite; transform-origin: 70% 70%; }
      `}</style>
    </div>
  );
}
